<script setup lang="ts">
import { useStickyOnIntersect } from '@/composables/useStickyOnIntersect';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { useHead } from '@unhead/vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ArticleSection from '@/components/common/ArticleSection/ArticleSection.vue';
import HeroSection from '@/components/common/HeroSection/HeroSection.vue';
import HowItWorksSection from '@/components/common/HowItWorksSection/HowItWorksSection.vue';
import LocationCarousel from '@/components/common/LocationCarousel/LocationCarousel.vue';
import MarketAtlasSection from '@/components/common/MarketAtlasSection/MarketAtlasSection.vue';
import QuestionSection from '@/components/common/QuestionSection/QuestionSection.vue';
import HomeOwnerWorldSection from '@/components/landing-page/HomeOwnerWorldSection/HomeOwnerWorldSection.vue';
import StickyCTA from '@/components/landing-page/StickyCTA/StickyCTA.vue';
import TeaserSection from '@/components/landing-page/TeaserSection/TeaserSection.vue';
import ValueSection from '@/components/landing-page/ValueSection/ValueSection.vue';

// Import helpers for mixins functionality
import { useRel } from '@/composables/useRel';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { HgSupportedLanguage } from '@smg-real-estate/domus';

const { t, locale } = useI18n();
const {
  getLandingPageCanonicalLink,
  getLandingPageAlternateLinks,
  getLandingPageDefaultAlternateLink,
} = useRel();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

const heroRef = ref<HTMLElement | null>(null);
const stickyCTA = useStickyOnIntersect(heroRef);

// Article mixin functionality
const isArticleVisible = computed(() => {
  if (isPlatformIs24()) {
    return locale.value !== HgSupportedLanguage.EN;
  }
  return true;
});

// Page meta methods
const getPageTitle = (): string => {
  return t('metaInfo.title') as string;
};

const getPageDescription = (): string => {
  return t('metaInfo.description') as string;
};

// Head configuration
const headConfig = computed(() => {
  const metaTags = [{ name: 'description', content: getPageDescription() }];
  const metaLinks = [];

  return {
    title: getPageTitle(),
    htmlAttrs: {
      lang: locale.value,
    },
    meta: metaTags,
    link: [
      ...metaLinks,
      getLandingPageCanonicalLink(),
      ...getLandingPageAlternateLinks(),
      getLandingPageDefaultAlternateLink(),
    ],
  };
});

useHead(headConfig);

// Lifecycle
onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'landingpage',
    locale: locale.value,
    stepInputs: '',
  });
});
</script>

<template>
  <div class="landingWrapper">
    <section ref="heroRef">
      <HeroSection class="hero-section" />
    </section>
    <div class="landing-content layout-container">
      <ValueSection />
      <HowItWorksSection />
      <TeaserSection />
      <QuestionSection />
      <MarketAtlasSection />
      <HomeOwnerWorldSection />
      <LocationCarousel />
      <ArticleSection v-if="isArticleVisible" />
    </div>
    <StickyCTA v-if="stickyCTA.showSticky.value" />
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/variables' as *;

.landing-content {
  > div,
  > section {
    margin-bottom: $space-500;
  }
}

.landingWrapper {
  position: relative;
  overflow: visible;
}

.hero-section {
  margin-bottom: $space-500;
}
</style>
